import React from 'react';
import { useNavigate } from 'react-router-dom';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#69f6b8',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
interface SignupPromptProps {
    isOpen: boolean;
    onClose: () => void;
    reason: 'save' | 'share' | 'read-limit' | 'like' | 'follow';
    remainingReads?: number;
}
 
const SignupPrompt: React.FC<SignupPromptProps> = ({ isOpen, onClose, reason, remainingReads }) => {
    const navigate = useNavigate();
 
    if (!isOpen) return null;
 
    const getContent = () => {
        switch (reason) {
            case 'save':       return { emoji: '💾', title: 'Save Your Story',       message: 'Create a free account to save your stories and access them anytime!' };
            case 'share':      return { emoji: '🌟', title: 'Share Your Story',      message: 'Create a free account to share your amazing stories with the community!' };
            case 'like':       return { emoji: '💜', title: 'Sign In to Like Stories!', message: 'Create a free account to like stories and support other authors.' };
            case 'follow':     return { emoji: '👥', title: 'Follow Your Favorite Writers!', message: 'Create a free account to follow writers and keep up with their stories.' };
            case 'read-limit': return { emoji: '📚', title: 'Keep Reading!',         message: `You've read ${remainingReads === 0 ? '10 stories' : 'many stories'}! Create a free account to keep reading unlimited stories.` };
            default:           return { emoji: '✨', title: 'Join Scriblish',        message: 'Create a free account to unlock all features!' };
        }
    };
 
    const content = getContent();
 
    const benefits = [
        'Save unlimited stories',
        'Read unlimited featured stories',
        'Share your work with the community',
        'Track your writing progress',
    ];
 
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: '20px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                animation: 'spFadeIn 0.2s ease',
            }}
        >
            <div style={{
                background: '#fff',
                borderRadius: '24px',
                padding: '40px 36px',
                maxWidth: '460px',
                width: '100%',
                boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
                border: `2px solid ${C.surfaceHigh}`,
                position: 'relative',
                animation: 'spSlideUp 0.25s ease',
            }}>
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute', top: '16px', right: '16px',
                        background: C.surfaceLow, border: 'none',
                        borderRadius: '50%', width: '32px', height: '32px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', fontSize: '1.1rem',
                        color: C.onSurfaceVariant, transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.primaryContainer; }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLow; }}
                >
                    ×
                </button>
 
                {/* Content */}
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3.5rem', marginBottom: '16px', animation: 'spBounce 0.5s ease' }}>
                        {content.emoji}
                    </div>
 
                    <h2 style={{
                        fontFamily: "'Epilogue', sans-serif",
                        fontSize: '1.6rem', fontWeight: '900',
                        color: C.onBackground, margin: '0 0 12px 0',
                    }}>
                        {content.title}
                    </h2>
 
                    <p style={{
                        fontSize: '0.95rem', color: C.onSurfaceVariant,
                        lineHeight: '1.6', margin: '0 0 24px 0',
                    }}>
                        {content.message}
                    </p>
 
                    {/* Benefits */}
                    <div style={{
                        background: C.primaryContainer,
                        borderRadius: '14px', padding: '18px 20px',
                        marginBottom: '24px', textAlign: 'left',
                    }}>
                        {benefits.map((b, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                fontSize: '0.88rem', color: C.onSurface, fontWeight: '600',
                                marginBottom: i < benefits.length - 1 ? '10px' : 0,
                            }}>
                                <div style={{
                                    width: '20px', height: '20px', borderRadius: '50%',
                                    background: C.primary, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.7rem', color: '#fff', flexShrink: 0,
                                }}>✓</div>
                                {b}
                            </div>
                        ))}
                    </div>
 
                    {/* Primary CTA */}
                    <button
                        onClick={() => navigate('/signup')}
                        style={{
                            width: '100%', padding: '14px',
                            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                            color: '#fff', border: 'none',
                            borderRadius: '9999px',
                            fontSize: '1rem', fontWeight: '700',
                            cursor: 'pointer', marginBottom: '14px',
                            fontFamily: "'Epilogue', sans-serif",
                            boxShadow: `0 4px 16px ${C.primary}40`,
                            transition: 'opacity 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1';   e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        Create Free Account
                    </button>
 
                    {/* Sign in link */}
                    <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant }}>
                        Already have an account?{' '}
                        <button
                            onClick={() => navigate('/login')}
                            style={{
                                background: 'none', border: 'none',
                                color: C.primary, fontWeight: '700',
                                cursor: 'pointer', fontSize: '0.85rem',
                                fontFamily: "'Epilogue', sans-serif",
                                padding: 0, transition: 'opacity 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            </div>
 
            <style>{`
                @keyframes spFadeIn  { from { opacity: 0; }                           to { opacity: 1; } }
                @keyframes spSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes spBounce  { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
            `}</style>
        </div>
    );
};
 
export default SignupPrompt;

